// pages/SapRouterLog.jsx — Penampil log SAProuter.
// ─────────────────────────────────────────────────────────────────────────────
// TAHAP A: membaca dan menampilkan saja. Belum ada penyimpanan, belum ada
// korelasi ke audit akses. Tujuannya memvalidasi asumsi parser terhadap log
// sungguhan sebelum membangun tabel dan poller di atasnya.
//
// KEPUTUSAN TATA LETAK:
//   Tabel "Sumber IP" diletakkan DI ATAS baris log mentah, dan diurutkan
//   menurut jumlah penolakan. Alasannya: sebuah tail 1000 baris terlalu panjang
//   untuk dibaca berurutan, dan yang dicari pembaca bukan kronologi melainkan
//   "IP mana yang berulang kali ditolak". Kronologinya tetap tersedia di bawah.
//
//   Baris yang gagal di-parse tetap ditampilkan (ditandai abu-abu), tidak
//   disembunyikan. Pada penampil log, membuang baris tak dikenal adalah cara
//   paling mudah menyembunyikan justru kejadian yang tidak biasa.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api as axiosApi } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
// Pola panel lipat dipakai bersama dengan SapLogonAudit — lihat
// components/CollapsiblePanel.jsx untuk keputusan yang melekat padanya.
import CollapsiblePanel from '../components/CollapsiblePanel';
import {
  Router, RefreshCw, Search, Loader2, AlertTriangle, Info, ShieldAlert, Server,
} from 'lucide-react';

const STR = {
  en: {
    title: 'SAProuter Log',
    subtitle: 'Inbound connections to SAProuter, with their real public source IP',
    note: 'Read live from /usr/sap/saprouter/saprouter.log via an SM69 external command. Nothing is stored — this is the tail of the file as it is right now.',
    conn: 'Host profile', reload: 'Reload', poll: 'Poll & correlate',
    noConn: 'SAProuter host profile not configured',
    polled: 'Stored and correlated',
    search: 'Search time / event / conn / IP / detail…',
    kind: 'Event type', all: 'All',
    connect_in: 'Inbound', connect_out: 'Outbound', established: 'Established',
    disconnect: 'Disconnect', denied: 'Denied', other: 'Other',
    lines: 'Lines', distinctIps: 'Source IPs',
    summary: 'Summary',
    sources: 'Source IPs (most rejected first)',
    ip: 'IP Address', hits: 'Lines', deniedC: 'Denied', last: 'Last seen',
    rdns: 'Reverse DNS', truncated: 'name truncated in log',
    raw: 'Log lines', time: 'Time', event: 'Event', connId: 'Conn', host: 'Host / detail',
    empty: 'No lines matched.',
    clearFilter: 'Clear filter',
    hint: 'Rejected attempts from unknown IPs are normal on an internet-facing SAProuter — saprouttab is doing its job. What matters is repetition from the same source.',
    shown: 'shown of',
    stored: 'Stored connections (used for correlation)',
    storedNote: 'These are what the audit page matches against. The live tail above shows only the last 1000 lines; anything older is gone unless it was stored here first.',
    connNo: 'Conn', src: 'Source', dst: 'Target', tConn: 'Connect', tDisc: 'Disconnect', open: 'still open',
    searchStored: 'Search connection / IP / rDNS / target…',
    storedNoMatch: 'No stored connection matched.',
    storedCapped: 'first 300 of',
    storedEmpty: 'Nothing stored yet — run "Poll & correlate", or enable the SAProuter collector schedule on the audit page.',
  },
  id: {
    title: 'Log SAProuter',
    subtitle: 'Koneksi masuk ke SAProuter, beserta IP publik asal yang sebenarnya',
    note: 'Dibaca langsung dari /usr/sap/saprouter/saprouter.log lewat external command SM69. Tidak ada yang disimpan — ini isi akhir berkas apa adanya saat ini.',
    conn: 'Profil host', reload: 'Muat ulang', poll: 'Poll & korelasi',
    noConn: 'Profil host SAProuter belum dikonfigurasi',
    polled: 'Tersimpan dan terkorelasi',
    search: 'Cari waktu / event / koneksi / IP / detail…',
    kind: 'Jenis event', all: 'Semua',
    connect_in: 'Masuk', connect_out: 'Keluar', established: 'Tersambung',
    disconnect: 'Putus', denied: 'Ditolak', other: 'Lainnya',
    lines: 'Baris', distinctIps: 'IP asal',
    summary: 'Ringkasan',
    sources: 'IP asal (paling banyak ditolak di atas)',
    ip: 'Alamat IP', hits: 'Baris', deniedC: 'Ditolak', last: 'Terakhir',
    rdns: 'Reverse DNS', truncated: 'nama terpotong di log',
    raw: 'Baris log', time: 'Waktu', event: 'Event', connId: 'Koneksi', host: 'Host / detail',
    empty: 'Tidak ada baris yang cocok.',
    clearFilter: 'Hapus filter',
    hint: 'Upaya yang ditolak dari IP tak dikenal itu wajar pada SAProuter yang menghadap internet — saprouttab bekerja sebagaimana mestinya. Yang perlu diperhatikan adalah pengulangan dari sumber yang sama.',
    shown: 'tampil dari',
    stored: 'Koneksi tersimpan (dipakai untuk korelasi)',
    storedNote: 'Inilah yang dicocokkan oleh halaman audit. Tail langsung di atas hanya memuat 1000 baris terakhir; yang lebih lama hilang kecuali sudah tersimpan di sini lebih dulu.',
    connNo: 'Koneksi', src: 'Asal', dst: 'Tujuan', tConn: 'Connect', tDisc: 'Disconnect', open: 'masih terbuka',
    searchStored: 'Cari koneksi / IP / rDNS / tujuan…',
    storedNoMatch: 'Tidak ada koneksi tersimpan yang cocok.',
    storedCapped: '300 pertama dari',
    storedEmpty: 'Belum ada yang tersimpan — jalankan "Poll & korelasi", atau nyalakan jadwal pengumpul SAProuter di halaman audit.',
  },
};

const KIND_STYLE = {
  connect_in:  { bg: 'rgb(var(--brand-50))', bd: '#93c5fd55', fg: 'rgb(var(--brand-700))' },
  connect_out: { bg: '#f0fdf4', bd: '#86efac55', fg: '#15803d' },
  established: { bg: '#ecfdf5', bd: '#6ee7b755', fg: '#047857' },
  disconnect:  { bg: '#f8fafc', bd: '#e2e8f0',   fg: '#475569' },
  denied:      { bg: '#fef2f2', bd: '#fca5a555', fg: '#b91c1c' },
  other:       { bg: '#faf5ff', bd: '#e9d5ff55', fg: '#7e22ce' },
};

const inputStyle = {
  padding: '7px 10px', borderRadius: 8, border: '1px solid #e2e8f0',
  fontSize: 12.5, color: '#0f172a', background: '#fff',
};

const fmtTs = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? String(iso)
    : d.toLocaleString('id-ID', { dateStyle: 'short', timeStyle: 'medium' });
};

function KindBadge({ kind, label }) {
  const st = KIND_STYLE[kind] || KIND_STYLE.other;
  return (
    <span style={{ padding: '2px 8px', borderRadius: 999, background: st.bg,
      border: `1px solid ${st.bd}`, color: st.fg, fontSize: 11, fontWeight: 600,
      whiteSpace: 'nowrap' }}>{label}</span>
  );
}

export default function SapRouterLog() {
  const { lang } = useLang();
  const T = STR[lang === 'en' ? 'en' : 'id'];

  const [conns, setConns]   = useState([]);
  const [connId, setConnId] = useState(0);
  const [data, setData]     = useState(null);
  const [kind, setKind]     = useState('all');
  const [q, setQ]           = useState('');
  const [loading, setLoad]  = useState(false);
  const [err, setErr]       = useState('');
  const [polling, setPolling] = useState(false);
  const [msg, setMsg]       = useState('');
  const [stored, setStored] = useState(null);
  const [qStored, setQStored] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await axiosApi.get('/api/sap/router-log/connections');
        const list = r.data?.connections || [];
        setConns(list);
        // Backend mengembalikan 200 dengan daftar kosong + penjelasan bila
        // SAPROUTER_CONN_ID belum diatur. Itu bukan galat HTTP, tetapi tetap
        // harus terlihat — tanpa ini halaman hanya diam dengan tabel kosong.
        if (!list.length && r.data?.error) setErr(r.data.error);
        // Bentuk fungsional: pilihan awal dibaca dari state SAAT ITU, bukan dari
        // nilai yang ditangkap closure. Dengan begitu connId tidak perlu jadi
        // dependensi efek — kalau ia dependensi, memilih profil lain akan
        // memicu pengambilan ulang daftar profil tanpa alasan.
        setConnId(prev => (prev || (list.length ? list[0].id : 0)));
      } catch (e) { setErr(e.response?.data?.error || e.message); }
    })();
  }, []);  // sekali saat mount

  const load = useCallback(async (id) => {
    if (!id) return;
    setLoad(true); setErr('');
    try {
      const r = await axiosApi.get(`/api/sap/router-log?connection_id=${id}`);
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
      setData(null);
    }
    setLoad(false);
  }, []);

  useEffect(() => { if (connId) load(connId); }, [connId, load]);

  const loadStored = useCallback(async () => {
    try {
      const r = await axiosApi.get('/api/sap/router-log/connections-stored?days=14');
      setStored(r.data);
    } catch (e) { /* tabel tersimpan bersifat pelengkap; kegagalannya tidak
                     boleh menutupi tampilan log langsung yang jadi inti halaman */ }
  }, []);
  useEffect(() => { loadStored(); }, [loadStored]);

  // Filter dilakukan di klien supaya mengganti jenis event tidak menembak SAP
  // lagi — satu tail sudah memuat semuanya.
  // Penyaringan baris log.
  // Dicocokkan ke baris mentah DAN ke kolom hasil parse. Mencari hanya di
  // `raw` terasa benar — di sanalah semua teksnya — tetapi kolom yang dilihat
  // pembaca sudah diolah: label event ("Denied") dan waktu terformat tidak ada
  // dalam baris mentah. Mengetik apa yang terlihat di layar lalu tidak
  // mendapat hasil adalah kegagalan yang paling membingungkan pada penampil
  // log, jadi keduanya dicocokkan.
  const rows = useMemo(() => {
    const all = data?.rows || [];
    const needle = q.trim().toLowerCase();
    return all.filter(r => {
      if (kind !== 'all' && r.kind !== kind) return false;
      if (!needle) return true;
      const hay = [
        r.raw, r.event, r.ip, r.detail,
        r.parsed ? `${r.side}${r.connNo}/${r.sub}` : '',
        r.ts ? fmtTs(r.ts) : '',
      ];
      return hay.some(v => v != null && String(v).toLowerCase().includes(needle));
    });
  }, [data, kind, q]);

  // Penyaringan koneksi tersimpan.
  // Disaring SEBELUM dipotong 300 baris. Kalau urutannya dibalik, mencari
  // sebuah IP hanya akan menelusuri 300 baris teratas dan mengembalikan
  // "tidak ada" untuk koneksi yang sebenarnya tersimpan — jawaban yang salah,
  // dan pada halaman audit itu jenis kesalahan yang paling mahal.
  //
  // Kotak pencarian di atas tidak dipakai ulang di sini: kotak itu menyaring
  // tail log langsung, dua sumber data yang berbeda. Satu kotak untuk dua
  // tabel akan membuat hasil yang kosong sulit ditafsirkan.
  const storedRows = useMemo(() => {
    const all = stored?.rows || [];
    const needle = qStored.trim().toLowerCase();
    if (!needle) return all;
    return all.filter(r =>
      [`c${r.conn_no}`, r.conn_no, r.src_ip, r.src_rdns, r.dst_host, r.dst_port]
        .some(v => v != null && String(v).toLowerCase().includes(needle)));
  }, [stored, qStored]);

  const byKind = data?.summary?.byKind || {};
  const ips    = data?.summary?.ips || [];

  return (
    <div style={{ padding: '18px 22px 40px', maxWidth: 1500, margin: '0 auto' }}>

      <div style={{ background: "linear-gradient(135deg, rgb(var(--brand-600)) 0%, rgb(var(--brand-400)) 100%)", borderRadius: 14, padding: '16px 22px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: 4 }}>
        <h1 style={{ fontSize: 20, fontWeight: 800, color: '#fff', margin: 0, letterSpacing: '-0.4px' }}>{T.title}</h1>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', marginTop: 3 }}>{T.subtitle}</div>
      </div>

      <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', padding: '10px 13px', borderRadius: 10,
        background: '#f8fafc', border: '1px solid #e2e8f0', margin: '14px 0 16px' }}>
        <Info size={14} color="#64748b" style={{ marginTop: 2, flexShrink: 0 }} />
        <div style={{ fontSize: 11.5, color: '#475569', lineHeight: 1.6 }}>{T.note}</div>
      </div>

      {err ? (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '10px 13px', borderRadius: 10,
          marginBottom: 14, background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: 12.5 }}>
          <AlertTriangle size={14} style={{ marginTop: 2, flexShrink: 0 }} />
          <div>{err}{data?.command ? ` (SM69: ${data.command})` : ''}</div>
        </div>
      ) : null}

      {/* Kontrol */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end', marginBottom: 14 }}>
        {/* Profil host BUKAN pilihan. SAProuter hanya berjalan di host Solman,
            jadi menampilkannya sebagai dropdown mengundang pengguna memilih
            host yang pasti gagal. Ditampilkan sebagai label supaya tetap
            terlihat host mana yang sedang dibaca — informasinya berguna, hanya
            kendalinya yang tidak. */}
        <div>
          <div style={{ fontSize: 11.5, fontWeight: 600, color: '#475569', marginBottom: 4 }}>{T.conn}</div>
          <div style={{ ...inputStyle, minWidth: 250, display: 'flex', alignItems: 'center', gap: 7,
            background: '#f8fafc', color: conns.length ? '#0f172a' : '#b91c1c', cursor: 'default' }}>
            <Server size={13} color={conns.length ? '#64748b' : '#b91c1c'} style={{ flexShrink: 0 }} />
            <span style={{ fontSize: 12.5 }}>
              {conns.length
                ? `${conns[0].sid} · ${conns[0].landscape || '—'} · ${conns[0].host} (cl.${conns[0].client})`
                : T.noConn}
            </span>
          </div>
        </div>
        <div>
          <div style={{ fontSize: 11.5, fontWeight: 600, color: '#475569', marginBottom: 4 }}>{T.kind}</div>
          <select value={kind} onChange={e => setKind(e.target.value)} style={{ ...inputStyle, minWidth: 150 }}>
            <option value="all">{T.all} ({data?.total ?? 0})</option>
            {['denied', 'connect_in', 'connect_out', 'established', 'disconnect', 'other']
              .filter(k => byKind[k])
              .map(k => <option key={k} value={k}>{T[k]} ({byKind[k]})</option>)}
          </select>
        </div>
        <button onClick={() => load(connId)} disabled={loading}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 10,
            border: '1px solid #16794622', background: '#167946', color: '#fff', fontSize: 12.5,
            fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1 }}>
          {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} {T.reload}
        </button>
        <button
          onClick={async () => {
            setPolling(true); setErr(''); setMsg('');
            try {
              const r = await axiosApi.post('/api/sap/router-log/poll', { connection_id: connId });
              const c = r.data.correlated || {};
              setMsg(`${T.polled}: ${r.data.inserted} + ${r.data.updated} · `
                + `${c.unique || 0} unique, ${c.ambiguous || 0} ambiguous, ${c.none || 0} none`);
              loadStored();
            } catch (e) { setErr(e.response?.data?.error || e.message); }
            setPolling(false);
          }}
          disabled={polling || !connId}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 10,
            border: '1px solid #e2e8f0', background: '#fff', color: '#334155', fontSize: 12.5,
            fontWeight: 600, cursor: polling ? 'not-allowed' : 'pointer', opacity: polling ? 0.6 : 1 }}>
          {polling ? <Loader2 size={13} className="animate-spin" /> : <ShieldAlert size={13} />} {T.poll}
        </button>
      </div>

      {msg ? (
        <div style={{ padding: '9px 13px', borderRadius: 10, marginBottom: 14, background: '#f0fdf4',
          border: '1px solid #bbf7d0', color: '#15803d', fontSize: 12.5 }}>{msg}</div>
      ) : null}

      {/* Ringkasan */}
      <CollapsiblePanel id="routerLog.summary" title={T.summary}
        meta={`${data?.total ?? 0} ${T.lines.toLowerCase()} · ${byKind.denied || 0} ${T.denied.toLowerCase()}`}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9, marginBottom: 8 }}>
        {[[T.lines, data?.total ?? '—'], [T.distinctIps, data?.summary?.distinctIps ?? '—'],
          [T.denied, byKind.denied || 0]].map(([l, v], i) => (
          <div key={i} style={{ padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 10,
            background: i === 2 && byKind.denied ? '#fef2f2' : '#f8fafc', minWidth: 108 }}>
            <div style={{ fontSize: 10.5, letterSpacing: '.05em', textTransform: 'uppercase', color: '#64748b' }}>{l}</div>
            <div style={{ fontSize: 21, fontWeight: 700, lineHeight: 1.25,
              color: i === 2 && byKind.denied ? '#b91c1c' : '#0f172a' }}>{v}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 7, alignItems: 'flex-start', fontSize: 11.5, color: '#94a3b8',
        lineHeight: 1.6, margin: '0 0 4px', maxWidth: 860 }}>
        <ShieldAlert size={13} style={{ marginTop: 2, flexShrink: 0 }} />
        <span>{T.hint}</span>
      </div>
      </CollapsiblePanel>

      {/* IP asal */}
      <CollapsiblePanel id="routerLog.sources" title={T.sources} meta={ips.length || null}>
      <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', background: '#fff',
        maxHeight: 300, overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              {[T.ip, T.rdns, T.hits, T.deniedC, T.last].map((h, i) => (
                <th key={i} style={{ padding: '9px 11px', textAlign: i ? 'center' : 'left', fontWeight: 600,
                  color: '#475569', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0,
                  background: '#f8fafc' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!ips.length ? (
              <tr><td colSpan={5} style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>—</td></tr>
            ) : ips.map(r => (
              <tr key={r.ip} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '7px 11px', fontFamily: 'ui-monospace,monospace', color: '#0f172a' }}>{r.ip}</td>
                {/* Reverse DNS menunjukkan MILIK SIAPA sebuah IP — hal yang tidak
                    terbaca dari angkanya, dan justru itu yang dicari saat menilai
                    apakah sebuah penolakan patut dikhawatirkan. */}
                <td style={{ padding: '7px 11px', color: r.rdns ? '#475569' : '#cbd5e1', fontSize: 11.5 }}>
                  {r.rdns || '—'}
                  {r.rdns && r.rdnsTruncated
                    ? <span title={T.truncated} style={{ color: '#f59e0b', marginLeft: 4 }}>…</span> : null}
                </td>
                <td style={{ padding: '7px 11px', textAlign: 'center', color: '#475569' }}>{r.hits}</td>
                <td style={{ padding: '7px 11px', textAlign: 'center',
                  color: r.denied ? '#b91c1c' : '#94a3b8', fontWeight: r.denied ? 700 : 400 }}>{r.denied || '—'}</td>
                <td style={{ padding: '7px 11px', textAlign: 'center', color: '#64748b', whiteSpace: 'nowrap' }}>{fmtTs(r.last)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </CollapsiblePanel>

      {/* Koneksi tersimpan — inilah yang dipakai korelasi, berbeda dari tail
          langsung di bawah. Dipisah eksplisit supaya tidak ada dugaan bahwa
          yang terlihat di layar otomatis sudah masuk basis data.
          Tertutup secara bawaan: isinya bisa ratusan baris dan hanya diperiksa
          saat menelusuri korelasi, sedangkan judul + jumlahnya sudah menjawab
          pertanyaan yang paling sering diajukan ("sudah tersimpan berapa?"). */}
      <CollapsiblePanel id="routerLog.stored" title={T.stored}
        meta={qStored.trim()
          ? `${storedRows.length} ${T.shown} ${stored?.rows?.length ?? 0}`
          : (stored?.count ?? 0)}
        note={T.storedNote} defaultOpen={false}>
      <div style={{ position: 'relative', maxWidth: 380, marginBottom: 8 }}>
        <Search size={14} color="#94a3b8" style={{ position: 'absolute', left: 10, top: 9 }} />
        <input value={qStored} onChange={e => setQStored(e.target.value)} placeholder={T.searchStored}
          style={{ ...inputStyle, width: '100%', paddingLeft: 30, paddingRight: 28 }} />
        {qStored ? (
          <button onClick={() => setQStored('')} aria-label="clear"
            style={{ position: 'absolute', right: 6, top: 6, border: 'none', background: 'transparent',
              color: '#94a3b8', cursor: 'pointer', fontSize: 15, lineHeight: 1, padding: '2px 4px' }}>×</button>
        ) : null}
      </div>
      <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', background: '#fff',
        maxHeight: 320, overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              {[T.connNo, T.src, T.dst, T.tConn, T.tDisc].map((h, i) => (
                <th key={i} style={{ padding: '9px 11px', textAlign: 'left', fontWeight: 600, color: '#475569',
                  whiteSpace: 'nowrap', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0,
                  background: '#f8fafc' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!stored?.rows?.length ? (
              <tr><td colSpan={5} style={{ padding: 20, textAlign: 'center', color: '#64748b', fontSize: 12 }}>
                {T.storedEmpty}</td></tr>
            ) : !storedRows.length ? (
              <tr><td colSpan={5} style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>
                {T.storedNoMatch}</td></tr>
            ) : storedRows.slice(0, 300).map((r, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', opacity: r.denied ? 0.65 : 1 }}>
                <td style={{ padding: '6px 11px', fontFamily: 'ui-monospace,monospace', color: '#475569' }}>
                  C{r.conn_no}</td>
                <td style={{ padding: '6px 11px' }}>
                  <span style={{ fontFamily: 'ui-monospace,monospace', color: '#0f172a' }}>{r.src_ip || '—'}</span>
                  {r.src_rdns ? <div style={{ fontSize: 10.5, color: '#94a3b8' }}>{r.src_rdns}</div> : null}
                </td>
                <td style={{ padding: '6px 11px', fontFamily: 'ui-monospace,monospace', color: '#475569',
                  whiteSpace: 'nowrap' }}>{r.dst_host || '—'}{r.dst_port ? ':' + r.dst_port : ''}</td>
                <td style={{ padding: '6px 11px', color: '#64748b', whiteSpace: 'nowrap' }}>{fmtTs(r.ts_connect)}</td>
                <td style={{ padding: '6px 11px', color: '#64748b', whiteSpace: 'nowrap' }}>
                  {r.ts_disconnect ? fmtTs(r.ts_disconnect)
                    : <span style={{ color: '#15803d', fontSize: 11 }}>{T.open}</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {storedRows.length > 300 ? (
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
          {T.storedCapped} {storedRows.length}
        </div>
      ) : null}
      </CollapsiblePanel>

      {/* Baris log.
          Kotak carinya diletakkan di sini, bukan di toolbar atas. Di toolbar ia
          tampak menyaring seluruh halaman padahal hanya menyentuh tabel ini —
          dan sesudah "Koneksi tersimpan" punya kotaknya sendiri, dua kotak yang
          berjauhan dengan cakupan berbeda jelas menyesatkan. Sekarang tiap
          kotak berdampingan dengan tabel yang disaringnya. */}
      <h2 style={{ fontSize: 14.5, fontWeight: 700, color: '#0f172a', margin: '0 0 8px' }}>
        {T.raw} <span style={{ fontSize: 11.5, fontWeight: 400, color: '#94a3b8' }}>
          — {rows.length} {T.shown} {data?.total ?? 0}</span>
      </h2>
      <div style={{ position: 'relative', maxWidth: 380, marginBottom: 8 }}>
        <Search size={14} color="#94a3b8" style={{ position: 'absolute', left: 10, top: 9 }} />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={T.search}
          style={{ ...inputStyle, width: '100%', paddingLeft: 30, paddingRight: 28 }} />
        {q ? (
          <button onClick={() => setQ('')} aria-label="clear"
            style={{ position: 'absolute', right: 6, top: 6, border: 'none', background: 'transparent',
              color: '#94a3b8', cursor: 'pointer', fontSize: 15, lineHeight: 1, padding: '2px 4px' }}>×</button>
        ) : null}
      </div>
      <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
        <div style={{ overflowX: 'auto', maxHeight: 620, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {[T.time, T.event, T.connId, T.ip, T.host].map((h, i) => (
                  <th key={i} style={{ padding: '9px 11px', textAlign: 'left', fontWeight: 600, color: '#475569',
                    whiteSpace: 'nowrap', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0,
                    background: '#f8fafc' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} style={{ padding: 26, textAlign: 'center', color: '#94a3b8' }}>
                  <Loader2 size={16} className="animate-spin" style={{ display: 'inline' }} /></td></tr>
              ) : !rows.length ? (
                <tr><td colSpan={5} style={{ padding: 26, textAlign: 'center', color: '#64748b' }}>
                  {T.empty}
                  {q.trim() || kind !== 'all' ? (
                    <button onClick={() => { setQ(''); setKind('all'); }}
                      style={{ marginLeft: 8, border: 'none', background: 'transparent', color: '#167946',
                        fontSize: 12, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>
                      {T.clearFilter}</button>
                  ) : null}
                </td></tr>
              ) : rows.map((r, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f1f5f9',
                  background: r.parsed ? 'transparent' : '#fafafa' }}>
                  <td style={{ padding: '6px 11px', color: '#64748b', whiteSpace: 'nowrap' }}>
                    {r.ts ? fmtTs(r.ts) : '—'}</td>
                  <td style={{ padding: '6px 11px' }}>
                    {r.parsed ? <KindBadge kind={r.kind} label={r.event} />
                      : <span style={{ color: '#94a3b8', fontSize: 11 }}>—</span>}</td>
                  <td style={{ padding: '6px 11px', fontFamily: 'ui-monospace,monospace', color: '#475569',
                    whiteSpace: 'nowrap' }}>{r.parsed ? `${r.side}${r.connNo}/${r.sub}` : ''}</td>
                  <td style={{ padding: '6px 11px', fontFamily: 'ui-monospace,monospace',
                    color: r.side === 'C' && r.ip ? '#0f172a' : '#94a3b8' }}>{r.ip || '—'}</td>
                  <td style={{ padding: '6px 11px', color: '#475569', fontFamily: r.parsed ? 'inherit' : 'ui-monospace,monospace',
                    fontSize: r.parsed ? 12 : 11.5 }}>
                    {r.parsed ? r.detail : r.raw}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
